import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import QueryString from 'qs';
import dayjs from 'dayjs';
import { getUserLessonsHistory } from '../../../../api/user';
import {
  attendMembersToClassroom,
  attendMembersToClassroomMiss,
  attendMembersToClassroomDelete,
} from '../../../../api/ticket';
import { errorMessages, formatDate } from '../../../../utils/methods';
import CommonLoading from '../../../../form-components/CommonLoading';
import CommonPagination from '../../../../form-components/CommonPagination';
import CommonDeleteModal from '../../../../form-components/CommonDeleteModal';
import ReservationEventTimeline, {
  ACTOR_TYPE_LABELS,
  ReservationHistoryToggle,
  formatActor,
} from '../../common/ReservationEventTimeline';

const PAGE_LIMIT = 10;

// 상태 색은 서로 헷갈리지 않게 색상환에서 떨어뜨린다.
// (예약을 주황 계열로 두면 결석 빨강과 구분이 안 돼 파랑으로 뺐다.)
const STATUS_META = {
  RESERVED: { label: '예약', color: '#1F6FEB', soft: '#DCE9FD' },
  ATTENDED: { label: '출석', color: '#2E7D32', soft: '#D8EDDA' },
  ABSENT: { label: '결석', color: '#C62828', soft: '#F7D9D9' },
  // strong = 표시 칩을 켰을 때 채움색. 흰 글씨 대비(4.5:1)가 모자란 색만 진하게 따로 둔다.
  WAITING: { label: '대기', color: '#B26A00', soft: '#FBEBD2', strong: '#8F5500' },
  CANCELED: { label: '취소', color: '#8B95A3', soft: '#E7EAEF', strong: '#5F6B7A' },
};

const STATUS_TOGGLES = ['RESERVED', 'ATTENDED', 'ABSENT', 'WAITING', 'CANCELED'];
// 개별 상태를 고르지 않은 상태(빈 배열)가 곧 '전체' 모드다.
// 전체 모드에서는 취소를 뺀 나머지를 모두 보여준다 — 지난 취소가 목록을 덮지 않게.
const DEFAULT_STATUSES = [];
// '전체'는 상태가 아니라 필터 해제 스위치라 색을 중립으로 둔다.
const ALL_META = { label: '전체', color: '#2B3038', soft: '#E9EDF3' };

// 사용 현황 막대는 잔여가 줄어드는 게이지다. 왼쪽부터 남은 몫 → 잡아둔 몫 → 쓴 몫 순으로 쌓아
// 쓸수록 왼쪽 파랑이 줄어들게 한다. 취소는 이용권을 쓰지 않아 막대에서 빠진다.
const USAGE_SEGMENTS = ['REMAINING', 'RESERVED', 'ABSENT', 'ATTENDED'];
// 잔여 구간은 상태가 아니라 남은 몫이라 별도 메타를 쓴다(헤더의 '예약 가능'과 같은 파랑).
const REMAINING_META = { label: '예약 가능', color: '#1F6FEB' };
const segmentMeta = statusKey =>
  statusKey === 'REMAINING' ? REMAINING_META : STATUS_META[statusKey];

// 개인/듀엣 레슨은 클래스명이 없어 수업 유형 라벨로 대신 표시한다.
const CLASS_TYPE_LABELS = {
  GROUP_LESSON: '그룹레슨',
  PRIVATE_LESSON: '개인레슨',
  DUET_LESSON: '듀엣레슨',
  OT: 'OT',
};

const displayClassName = lesson =>
  lesson.className || CLASS_TYPE_LABELS[lesson.courseClassType] || '-';

// 취소 건은 되돌릴 처리가 없어 선택 자체를 막는다.
const isSelectable = lesson => lesson.status !== 'CANCELED';
// 출결 처리·정정 대상. 대기(WAITING)는 예약이 확정된 상태가 아니라 제외한다.
const isAttendanceEditable = lesson =>
  ['RESERVED', 'ATTENDED', 'ABSENT'].includes(lesson.status);
// 대기 취소도 같은 API 가 처리한다.
const isCancelable = lesson => lesson.status !== 'CANCELED';

// 하단 액션 정의 — 확인 모달 문구와 실제 호출을 한곳에 모은다.
const ACTIONS = {
  attend: {
    label: '출석 처리',
    isTarget: isAttendanceEditable,
    call: attendMembersToClassroom,
    confirmVariant: 'submit',
    title: count => `선택한 ${count}건을 출석 처리할까요?`,
    description: '수업에 설정된 차감 횟수만큼 이용권이 차감됩니다.',
    successMessage: '출석 처리되었습니다',
    emptyMessage: '출석 처리할 수 있는 예약이 없습니다',
  },
  miss: {
    label: '결석 처리',
    isTarget: isAttendanceEditable,
    call: attendMembersToClassroomMiss,
    confirmVariant: 'submit',
    title: count => `선택한 ${count}건을 결석 처리할까요?`,
    description: '결석도 출석과 동일하게 이용권 횟수가 차감됩니다.',
    successMessage: '결석 처리되었습니다',
    emptyMessage: '결석 처리할 수 있는 예약이 없습니다',
  },
  cancel: {
    label: '예약 취소',
    isTarget: isCancelable,
    call: attendMembersToClassroomDelete,
    confirmVariant: 'delete',
    title: count => `선택한 ${count}건의 예약을 취소할까요?`,
    description:
      '출석·결석이 확정된 건을 취소하면 차감된 횟수가 복구됩니다. ' +
      '개인·듀엣 레슨은 수업 회차까지 삭제되어 내역에서 사라집니다.',
    successMessage: '예약이 취소되었습니다',
    emptyMessage: '취소할 수 있는 예약이 없습니다',
  },
};

const HELP_TEXT =
  '횟수 차감은 예약할 때가 아니라 출석·결석이 확정될 때 이루어집니다. ' +
  '막대의 예약 구간은 아직 차감되지 않았지만 자리를 잡아둔 몫이에요.';

const DATE_FORMAT = 'YY.MM.DD (dd) HH:mm';
// 처리일시는 초 단위까지 봐야 순서를 가릴 수 있어 따로 둔다.
const PROCESSED_FORMAT = 'YY.MM.DD HH:mm:ss';

const BAND_PX = '24px';

// colgroup 고정폭 합계. 모달보다 넓으면 가로 스크롤이 생긴다.
const TABLE_MIN_WIDTH = 44 + 150 + 140 + 178 + 80 + 72 + 126 + 150 + 126 + 140 + 82 + 56;
// 공유 수강권(#158) "예약 회원" 열 — 이름 + "공유" 배지
const SHARED_MEMBER_COL_WIDTH = 130;

const headCellSx = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#8A93A0',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #E9EDF3',
  backgroundColor: '#FBFCFE',
  py: '10px',
  // TableSortLabel 은 라벨과 정렬 화살표가 한 flex 라, 그대로 가운데로 모으면
  // 화살표 폭만큼 글자가 왼쪽으로 밀려 본문 값과 축이 어긋난다.
  // 화살표를 흐름에서 빼내 글자만 셀 가운데에 오게 한다.
  '& .MuiTableSortLabel-root': { position: 'relative', width: '100%', justifyContent: 'center' },
  '& .MuiTableSortLabel-icon': { position: 'absolute', right: 0, margin: 0 },
};
const bodyCellSx = {
  fontSize: '13px',
  color: '#2B3038',
  borderBottom: '1px solid #F2F4F8',
  py: 0,
  height: '44px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const actionButtonSx = {
  height: '34px',
  px: '15px',
  fontSize: '13px',
  fontWeight: 600,
  borderRadius: '7px',
  whiteSpace: 'nowrap',
  transition: 'background-color 160ms ease, border-color 160ms ease',
};

/**
 * 표시 토글 하나 — 색 점 + 라벨 + 건수.
 * 꺼짐도 흐리게 하지 않고 테두리·그림자가 있는 버튼으로 둬 누를 수 있다는 걸 드러내고,
 * 켜짐은 상태색으로 채우고 체크를 붙여 선택됐음을 분명히 한다.
 */
const StatusToggle = ({ meta, count, active, onToggle }) => {
  const strong = meta.strong || meta.color;
  return (
    <Stack
      role="button"
      tabIndex={0}
      aria-pressed={active}
      onClick={onToggle}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: '6px',
        height: '30px',
        px: '11px',
        borderRadius: '15px',
        cursor: 'pointer',
        userSelect: 'none',
        border: '1px solid',
        borderColor: active ? strong : '#CBD2DB',
        backgroundColor: active ? strong : '#fff',
        boxShadow: active ? `0 2px 6px ${strong}40` : '0 1px 2px rgba(16, 24, 40, 0.06)',
        transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 100ms ease',
        '&:hover': active
          ? { boxShadow: `0 3px 8px ${strong}55` }
          : { borderColor: strong, backgroundColor: meta.soft },
        '&:active': { transform: 'scale(0.96)' },
        '&:focus-visible': { outline: `2px solid ${meta.color}`, outlineOffset: '2px' },
      }}>
      {active ? (
        <CheckIcon sx={{ width: '14px', height: '14px', color: '#fff', flexShrink: 0 }} />
      ) : (
        <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0, backgroundColor: meta.color }} />
      )}
      <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: active ? '#fff' : '#3A414B' }}>
        {meta.label}
      </Typography>
      {count !== undefined && (
        <Typography
          sx={{
            fontSize: '11.5px',
            fontWeight: 700,
            lineHeight: '18px',
            minWidth: '18px',
            px: '5px',
            borderRadius: '9px',
            textAlign: 'center',
            color: strong,
            backgroundColor: active ? '#fff' : meta.soft,
          }}>
          {count}
        </Typography>
      )}
    </Stack>
  );
};

/** 헤더 메타 라인 조각 — 가운뎃점으로 구분되는 인라인 텍스트. */
const MetaText = ({ children }) => (
  <Typography sx={{ fontSize: '12.5px', color: '#7B8494', whiteSpace: 'nowrap' }}>{children}</Typography>
);

const MetaDot = () => (
  <Box sx={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#C9CFD8' }} />
);

/**
 * 수강권/일일권 카드의 "예약 내역" 버튼으로 여는 예약·수업 내역 모달.
 *
 * 구조: 헤더(상품·기간) / 사용 현황 막대 / 상태 토글 툴바 / 스크롤되는 표 / 바닥에 고정된 액션 바.
 * 행을 선택해 출석·결석·예약취소를 처리할 수 있고, 수업 행을 클릭하면 스케줄 화면으로 이동한다.
 *
 * kind: 'course'(수강권) | 'trial'(일일권)
 */
export default function TicketLessonHistoryModal({ open, onClose, ticket, kind, user, bigUser, onMutated }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [activeStatuses, setActiveStatuses] = useState(DEFAULT_STATUSES);
  const [sortDirection, setSortDirection] = useState('DESC');
  const [lessons, setLessons] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  // [이력] 펼친 예약 하나 — 다른 행을 펼치면 접힌다
  const [expandedId, setExpandedId] = useState(null);
  const [pendingActionKey, setPendingActionKey] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 공유 수강권(#158) — 기본은 공유 회원 예약까지 전부, 켜면 이 회원 예약만
  const [onlyMine, setOnlyMine] = useState(false);
  // 사용 현황 막대는 수강권 전체 기준 — "내 예약만"을 켜도 마지막 전체 요약을 그대로 쓴다
  const [ticketSummary, setTicketSummary] = useState(null);

  const isTrial = kind === 'trial';
  const isSharedTicket = !isTrial && (!!ticket?.sharedFrom || (ticket?.sharedMembers ?? []).length > 0);
  const ticketIdParam = isTrial
    ? { memberTrialTicketId: ticket?.memberTrialTicketId }
    : { memberCourseTicketId: ticket?.memberCourseTicketId };
  const ticketId = isTrial ? ticket?.memberTrialTicketId : ticket?.memberCourseTicketId;
  const ticketName = isTrial ? ticket?.trialName : ticket?.ticketName;
  const classTypeLabel = ticket?.classType ? t(ticket.classType) : null;

  const totalCount = Number(ticket?.usageCount) || 0;
  const usedCount = Number(ticket?.usedCount) || 0;
  const reservedCount = Number(ticket?.reservedCount) || 0;
  const remainingCount = Math.max(totalCount - usedCount, 0);
  const effectiveRemaining = Math.max(remainingCount - reservedCount, 0);
  // 기간제 일일권(PERIOD)은 횟수 개념이 없어 막대를 그리지 않는다.
  const showUsageBar = totalCount > 0 && ticket?.usageType !== 'PERIOD';

  // 카드와 같은 이름으로 보이도록 회차·기간을 붙인다.
  const periodLabel =
    ticket?.usagePeriod == null
      ? null
      : `${ticket.usagePeriod}${ticket.usagePeriodType === 'MONTH' ? '개월' : '일'}`;
  const displayTitle = [ticketName, totalCount > 0 ? `${totalCount}회` : null, periodLabel]
    .filter(Boolean)
    .join(' ');

  const periodText =
    ticket?.issueDate && ticket?.expireDate
      ? `${ticket.issueDate.replaceAll('-', '.')} ~ ${ticket.expireDate.replaceAll('-', '.')}`
      : null;
  const daysRemaining = ticket?.expireDate
    ? dayjs(ticket.expireDate).startOf('day').diff(dayjs().startOf('day'), 'day')
    : null;

  const summary = lessons?.summary;
  const statusCounts = {
    RESERVED: summary?.reservedCount,
    ATTENDED: summary?.attendedCount,
    ABSENT: summary?.absentCount,
    CANCELED: summary?.canceledCount,
    WAITING: summary?.waitingCount,
  };

  // 개별 선택이 없으면 전체 모드. 전체 건수는 기본 조회 범위(취소 제외)와 맞춘다.
  const isAllMode = activeStatuses.length === 0;
  const totalStatusCount = STATUS_TOGGLES.filter(statusKey => statusKey !== 'CANCELED').reduce(
    (sum, statusKey) => sum + (Number(statusCounts[statusKey]) || 0),
    0,
  );

  const getLessons = async () => {
    setIsLoading(true);
    try {
      const queryString = QueryString.stringify(
        {
          page,
          limit: PAGE_LIMIT,
          sortBy: 'CLASS_DATE',
          sortDirection,
          // 전체 모드는 상태를 넘기지 않고 백엔드 기본(취소 제외)에 맡긴다.
          ...(isAllMode ? { includeCanceled: false } : { statuses: activeStatuses }),
          ...ticketIdParam,
          ...(isSharedTicket && onlyMine ? { onlyMine: true } : {}),
        },
        { arrayFormat: 'repeat' },
      );
      const data = await getUserLessonsHistory(user.memberId, queryString, bigUser.token);
      setLessons(data);
      if (!(isSharedTicket && onlyMine)) setTicketSummary(data?.summary ?? null);
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && ticketId && user?.memberId) {
      getLessons();
    }
  }, [open, ticketId, page, activeStatuses, sortDirection, onlyMine]);

  // 다른 카드로 다시 열 때 이전 카드의 페이지/필터/선택이 남지 않도록 초기화.
  useEffect(() => {
    if (open) {
      setPage(1);
      setActiveStatuses(DEFAULT_STATUSES);
      setSortDirection('DESC');
      setOnlyMine(false);
      setTicketSummary(null);
      setLessons(null);
      setSelectedIds([]);
    }
  }, [open, ticketId]);

  const rows = useMemo(() => lessons?.content ?? [], [lessons]);

  // 목록이 바뀌면 화면에 없는 행의 선택은 버린다.
  useEffect(() => {
    setSelectedIds(prev => prev.filter(id => rows.some(row => row.reservationId === id)));
  }, [rows]);

  const selectableRows = useMemo(() => rows.filter(isSelectable), [rows]);
  const selectedRows = useMemo(
    () => rows.filter(row => selectedIds.includes(row.reservationId)),
    [rows, selectedIds],
  );
  const allSelected = selectableRows.length > 0 && selectedIds.length === selectableRows.length;

  const toggleStatus = statusKey => {
    setActiveStatuses(prev =>
      prev.includes(statusKey) ? prev.filter(value => value !== statusKey) : [...prev, statusKey],
    );
    setPage(1);
  };

  const toggleRow = lesson => {
    setSelectedIds(prev =>
      prev.includes(lesson.reservationId)
        ? prev.filter(id => id !== lesson.reservationId)
        : [...prev, lesson.reservationId],
    );
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : selectableRows.map(row => row.reservationId));
  };

  // 다지점 공유 회원권이면 다른 지점 수업이 섞여 나온다. 스케줄 화면은 지점 단위라
  // 타 지점 수업으로 이동하면 지금 보는 지점과 어긋난 화면이 뜬다 → 이동을 막는다.
  const currentBranchId = bigUser?.branch?.branchId;
  const isOtherBranch = lesson =>
    !!lesson.branchId && !!currentBranchId && lesson.branchId !== currentBranchId;

  // 행 클릭 → 해당 수업의 스케줄 화면으로 이동 + 포커스 플래시. 상태와 무관하게
  // 수업 정보가 남아 있으면 이동한다 (개인/듀엣 취소는 회차가 삭제돼 정보가 없을 수 있다).
  const canOpenSchedule = lesson =>
    !!lesson.courseClassTimeId && !!lesson.classDateTime && !isOtherBranch(lesson);

  const handleRowClick = lesson => {
    if (!canOpenSchedule(lesson)) return;
    const date = formatDate(lesson.classDateTime, 'YYYY-MM-DD');
    if (lesson.courseClassType === 'GROUP_LESSON') {
      navigate(`/group?focusClassTimeId=${lesson.courseClassTimeId}&focusDate=${date}`);
    } else {
      navigate(`/private?date=${date}&focusClassTimeId=${lesson.courseClassTimeId}`);
    }
  };

  // 가로 드래그 스크롤. 표가 모달보다 넓을 때만 의미가 있다.
  const scrollRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDragStart = event => {
    const container = scrollRef.current;
    if (!container || container.scrollWidth <= container.clientWidth) return;
    // 체크박스·버튼 위에서 시작한 클릭은 그대로 두고 빈 영역에서만 끈다
    if (event.target.closest('input, button, a')) return;

    const startX = event.clientX;
    const startScroll = container.scrollLeft;
    let moved = false;

    const onMove = moveEvent => {
      const delta = moveEvent.clientX - startX;
      if (!moved && Math.abs(delta) < 5) return;
      moved = true;
      setDragging(true);
      container.scrollLeft = startScroll - delta;
    };
    const onUp = upEvent => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp, true);
      setDragging(false);
      // 끌고 나서 손을 떼면 그 자리의 행 클릭이 따라오지 않게 막는다
      if (moved) {
        upEvent.preventDefault();
        upEvent.stopPropagation();
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp, true);
  };

  const pendingAction = pendingActionKey ? ACTIONS[pendingActionKey] : null;
  const pendingTargets = pendingAction ? selectedRows.filter(pendingAction.isTarget) : [];

  const openActionConfirm = actionKey => {
    const action = ACTIONS[actionKey];
    if (selectedRows.filter(action.isTarget).length === 0) {
      toast.error(t(action.emptyMessage));
      return;
    }
    setPendingActionKey(actionKey);
  };

  /**
   * 확인한 액션을 실제로 호출한다.
   * 출결·취소 API 는 모두 수업 회차(courseClassTimeId) 단위라 선택 행을 회차별로 묶어 호출한다.
   * 한 회차가 실패해도 나머지는 진행하고 결과를 합산해 알린다.
   */
  const runPendingAction = async () => {
    const action = pendingAction;
    const groups = new Map();
    pendingTargets.forEach(row => {
      const ids = groups.get(row.courseClassTimeId) ?? [];
      ids.push(row.reservationId);
      groups.set(row.courseClassTimeId, ids);
    });

    setIsSubmitting(true);
    let successCount = 0;
    let failedCount = 0;
    let lastError = null;
    for (const [courseClassTimeId, memberReservationIDs] of groups) {
      try {
        await action.call(courseClassTimeId, { memberReservationIDs }, bigUser.token);
        successCount += memberReservationIDs.length;
      } catch (error) {
        failedCount += memberReservationIDs.length;
        lastError = error;
      }
    }
    setIsSubmitting(false);
    setPendingActionKey(null);
    setSelectedIds([]);

    if (successCount > 0) {
      toast.success(`${successCount}건 ${t(action.successMessage)}`);
    }
    if (failedCount > 0) {
      toast.error(`${failedCount}건 실패 - ${errorMessages(lastError)}`);
    }
    await getLessons();
    if (onMutated) await onMutated();
  };

  const hasSelection = selectedIds.length > 0;

  // 사용 현황 막대 구간. 총 횟수 대비 비율이라 합이 100%를 넘지 않도록 잔여로 흡수한다.
  const segmentValues = {
    REMAINING: effectiveRemaining,
    RESERVED: reservedCount,
    ABSENT: Number((ticketSummary ?? summary)?.absentCount) || 0,
    ATTENDED: Number((ticketSummary ?? summary)?.attendedCount) || 0,
  };
  const usageSegments = USAGE_SEGMENTS.map(statusKey => ({
    statusKey,
    value: segmentValues[statusKey],
  })).filter(segment => segment.value > 0);
  const usedSegmentTotal = usageSegments.reduce((sum, segment) => sum + segment.value, 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '14px',
          // 바닥 액션 바를 고정하려면 높이를 잡고 표만 스크롤시켜야 한다.
          height: 'min(88vh, 860px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}>
      {/* ── 헤더: 상품 정체성 + 기간 ─────────────────────────── */}
      <Stack sx={{ flexShrink: 0, px: BAND_PX, pt: '18px', pb: '14px' }}>
        <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '9px', minWidth: 0 }}>
          {classTypeLabel && (
            <Box
              component="span"
              sx={{
                px: '8px',
                py: '3px',
                borderRadius: '5px',
                fontSize: '11.5px',
                fontWeight: 600,
                color: '#4A6FA5',
                backgroundColor: '#EEF3FB',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}>
              {classTypeLabel}
            </Box>
          )}
          <Typography
            sx={{
              fontSize: '17.5px',
              fontWeight: 700,
              color: '#1C2027',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {displayTitle}
          </Typography>
          <Tooltip
            title={HELP_TEXT}
            placement="bottom-start"
            arrow
            componentsProps={{ tooltip: { sx: { fontSize: '12.5px', lineHeight: 1.55, maxWidth: '300px' } } }}>
            <HelpOutlineIcon sx={{ fontSize: '16px', color: '#A6ADB8', cursor: 'help', flexShrink: 0 }} />
          </Tooltip>

          <Stack
            sx={{ flexDirection: 'row', alignItems: 'center', gap: '9px', ml: 'auto', pl: '16px', flexShrink: 0 }}>
            {periodText && <MetaText>{periodText}</MetaText>}
            {periodText && daysRemaining !== null && <MetaDot />}
            {daysRemaining !== null && (
              <Typography
                sx={{
                  fontSize: '15px',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  color: daysRemaining < 0 ? '#8A93A0' : '#1C6E4A',
                }}>
                {daysRemaining < 0 ? t('기간 만료') : `${t('만료까지')} ${daysRemaining}일`}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>

      {/* ── 사용 현황 막대: 총 횟수를 출석/결석/예약/잔여로 쪼개 보여준다 ── */}
      {showUsageBar && (
        <Box sx={{ flexShrink: 0, px: BAND_PX, pb: '16px' }}>
          <Stack
            sx={{
              flexDirection: 'row',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              mb: '7px',
            }}>
            <Typography sx={{ fontSize: '12.5px', color: '#7B8494' }}>{t('사용 현황')}</Typography>
            {/* 잔여(=총-사용)와 예약 가능(=잔여-예약중)은 다른 수다. 예약중이 없으면 둘이 같으므로
                같은 숫자를 두 번 쓰지 않고 잔여만 남긴다. */}
            <Stack sx={{ flexDirection: 'row', alignItems: 'baseline', gap: '5px' }}>
              <Typography sx={{ fontSize: '12.5px', color: '#7B8494' }}>{t('잔여')}</Typography>
              <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#2B3038', lineHeight: 1.1 }}>
                {remainingCount}
              </Typography>
              {reservedCount > 0 && (
                <>
                  <MetaDot />
                  <Typography sx={{ fontSize: '12.5px', color: '#7B8494' }}>{t('예약 가능')}</Typography>
                  <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#1F6FEB', lineHeight: 1.1 }}>
                    {effectiveRemaining}
                  </Typography>
                </>
              )}
              <Typography sx={{ fontSize: '12.5px', color: '#7B8494' }}>
                {`/ ${t('총')} ${totalCount}회`}
              </Typography>
            </Stack>
          </Stack>
          <Stack
            sx={{
              flexDirection: 'row',
              height: '10px',
              borderRadius: '5px',
              overflow: 'hidden',
              backgroundColor: '#EDF0F4',
            }}>
            {usageSegments.map(segment => (
              <Tooltip
                key={segment.statusKey}
                title={`${segmentMeta(segment.statusKey).label} ${segment.value}회`}
                arrow>
                <Box
                  sx={{
                    width: `${(segment.value / Math.max(totalCount, usedSegmentTotal)) * 100}%`,
                    backgroundColor: segmentMeta(segment.statusKey).color,
                    // 예약 구간은 아직 차감 전이라 채도를 낮춰 확정분과 구분한다.
                    opacity: segment.statusKey === 'RESERVED' ? 0.45 : 1,
                    transition: 'width 200ms ease',
                  }}
                />
              </Tooltip>
            ))}
          </Stack>
          {/* 범례. 툴팁을 올려봐야 색의 뜻을 아는 문제를 없앤다. 막대와 같은 usageSegments 를 써서
              색·라벨·수치가 어긋날 수 없게 한다. */}
          <Stack sx={{ flexDirection: 'row', flexWrap: 'wrap', gap: '3px 14px', mt: '9px' }}>
            {usageSegments.map(segment => (
              <Stack
                key={segment.statusKey}
                sx={{ flexDirection: 'row', alignItems: 'center', gap: '5px' }}>
                <Box
                  sx={{
                    width: '9px',
                    height: '9px',
                    borderRadius: '2px',
                    backgroundColor: segmentMeta(segment.statusKey).color,
                    opacity: segment.statusKey === 'RESERVED' ? 0.45 : 1,
                  }}
                />
                <Typography sx={{ fontSize: '12px', color: '#7B8494' }}>
                  {segmentMeta(segment.statusKey).label}
                </Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#2B3038' }}>
                  {segment.value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {/* ── 툴바: 상태 토글 + 총 건수 ─────────────────────────── */}
      <Stack
        sx={{
          flexShrink: 0,
          flexDirection: 'row',
          alignItems: 'center',
          gap: '7px',
          px: BAND_PX,
          py: '12px',
          borderTop: '1px solid #EDF0F4',
          backgroundColor: '#FAFBFD',
        }}>
        <Typography sx={{ fontSize: '12.5px', color: '#8A93A0', mr: '2px', whiteSpace: 'nowrap' }}>
          {t('표시')}
        </Typography>
        <StatusToggle
          meta={ALL_META}
          count={totalStatusCount}
          active={isAllMode}
          onToggle={() => {
            setActiveStatuses([]);
            setPage(1);
          }}
        />
        <Box sx={{ width: '1px', height: '18px', backgroundColor: '#E4E8EE', mx: '2px' }} />
        {STATUS_TOGGLES.map(statusKey => (
          <StatusToggle
            key={statusKey}
            meta={STATUS_META[statusKey]}
            count={statusCounts[statusKey]}
            active={activeStatuses.includes(statusKey)}
            onToggle={() => toggleStatus(statusKey)}
          />
        ))}
        {isSharedTicket && (
          <>
            <Box sx={{ width: '1px', height: '18px', backgroundColor: '#E4E8EE', mx: '2px' }} />
            <FormControlLabel
              sx={{ ml: 0, mr: 0, '& .MuiFormControlLabel-label': { fontSize: '12.5px', color: '#5C6470' } }}
              control={(
                <Checkbox
                  size="small"
                  checked={onlyMine}
                  onChange={event => {
                    setOnlyMine(event.target.checked);
                    setPage(1);
                  }}
                  sx={{ p: '4px', color: '#B8C0CC', '&.Mui-checked': { color: '#4AB3BC' } }}
                />
              )}
              label={t('내 예약만')}
            />
          </>
        )}
        <Typography sx={{ ml: 'auto', fontSize: '12.5px', color: '#7B8494', whiteSpace: 'nowrap' }}>
          총 <Box component="span" sx={{ fontWeight: 700, color: '#2B3038' }}>{(lessons?.totalElements ?? 0).toLocaleString()}</Box>건
        </Typography>
      </Stack>

      {/* ── 표: 이 영역만 세로로 스크롤된다 ───────────────────── */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: BAND_PX, py: '14px' }}>
        {isLoading && !lessons ? (
          <CommonLoading minHeight={360} />
        ) : (
          <TableContainer
            ref={scrollRef}
            onMouseDown={handleDragStart}
            sx={{
              border: '1px solid #E9EDF3',
              borderRadius: '10px',
              overflowX: 'auto',
              cursor: dragging ? 'grabbing' : 'auto',
              userSelect: dragging ? 'none' : 'auto',
            }}>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: `${TABLE_MIN_WIDTH + (isSharedTicket ? SHARED_MEMBER_COL_WIDTH : 0)}px` }}>
              <colgroup>
                <col style={{ width: '44px' }} />
                {/* 수업명 — 그룹레슨만 실제 이름이 있고 개인·듀엣은 유형 라벨이라 정보량이 적다 */}
                <col style={{ width: '150px' }} />
                {isSharedTicket && <col style={{ width: `${SHARED_MEMBER_COL_WIDTH}px` }} />}
                <col style={{ width: '140px' }} />
                <col style={{ width: '178px' }} />
                <col style={{ width: '80px' }} />
                <col style={{ width: '72px' }} />
                <col style={{ width: '126px' }} />
                <col style={{ width: '150px' }} />
                <col style={{ width: '126px' }} />
                <col style={{ width: '140px' }} />
                <col style={{ width: '82px' }} />
                <col style={{ width: '56px' }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ ...headCellSx, px: 0 }}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={hasSelection && !allSelected}
                      disabled={selectableRows.length === 0}
                      onChange={toggleAll}
                      inputProps={{ 'aria-label': '전체 선택' }}
                      sx={{ p: '4px' }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={headCellSx}>{t('수업명')}</TableCell>
                  {isSharedTicket && <TableCell align="center" sx={headCellSx}>{t('예약 회원')}</TableCell>}
                  <TableCell align="center" sx={headCellSx}>{t('예약한 지점')}</TableCell>
                  <TableCell align="center" sortDirection={sortDirection.toLowerCase()} sx={headCellSx}>
                    <TableSortLabel
                      active
                      direction={sortDirection.toLowerCase()}
                      onClick={() => {
                        setSortDirection(prev => (prev === 'DESC' ? 'ASC' : 'DESC'));
                        setPage(1);
                      }}>
                      {t('수업일시')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={headCellSx}>{t('담당강사')}</TableCell>
                  <TableCell align="center" sx={headCellSx}>{t('상태')}</TableCell>
                  <TableCell align="center" sx={headCellSx}>{t('예약자')}</TableCell>
                  <TableCell align="center" sx={headCellSx}>{t('처리일시')}</TableCell>
                  <TableCell align="center" sx={headCellSx}>{t('처리자')}</TableCell>
                  <TableCell align="center" sx={headCellSx}>{t('처리지점')}</TableCell>
                  <TableCell align="center" sx={headCellSx}>{t('차감')}</TableCell>
                  <TableCell align="center" sx={headCellSx}>{t('이력')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(lesson => {
                  const meta = STATUS_META[lesson.status];
                  const otherBranch = isOtherBranch(lesson);
                  const clickable = canOpenSchedule(lesson);
                  const checked = selectedIds.includes(lesson.reservationId);
                  const expanded = expandedId === lesson.reservationId;
                  return (
                    <React.Fragment key={lesson.reservationId}>
                    <TableRow
                      title={otherBranch ? `${lesson.branchName} 수업입니다. 스케줄 이동은 해당 지점에서 가능합니다.` : undefined}
                      onClick={() => handleRowClick(lesson)}
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        ...(checked && { backgroundColor: '#F1F6FE' }),
                        ...(clickable && {
                          cursor: 'pointer',
                          transition: 'background-color 160ms ease',
                          '&:hover': { backgroundColor: checked ? '#E8F1FD' : '#F7F9FC' },
                          '&:hover .row-go': { opacity: 1 },
                        }),
                      }}>
                      <TableCell align="center" sx={{ ...bodyCellSx, px: 0 }} onClick={event => event.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={checked}
                          disabled={!isSelectable(lesson)}
                          onChange={() => toggleRow(lesson)}
                          inputProps={{ 'aria-label': '행 선택' }}
                          sx={{ p: '4px' }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ ...bodyCellSx, position: 'relative', pl: '10px', pr: clickable ? '18px' : '10px' }}>
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            // 유형 라벨 대체 표시는 실제 수업명과 구분되게 옅게
                            ...(lesson.className ? {} : { color: '#8A93A0' }),
                          }}>
                          {displayClassName(lesson)}
                        </Box>
                        {/* 화살표는 절대배치 — 이름 중앙 정렬을 밀지 않는다 */}
                        {clickable && (
                          <Tooltip title="스케줄에서 보기" placement="top" arrow>
                            <ArrowForwardIcon
                              className="row-go"
                              sx={{
                                position: 'absolute',
                                right: '4px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: '14px',
                                color: '#1F6FEB',
                                opacity: 0,
                                transition: 'opacity 160ms ease',
                              }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      {isSharedTicket && (
                        <TableCell align="center" sx={bodyCellSx}>
                          <Stack sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                            <span>{lesson.reservedMemberName || '-'}</span>
                            {lesson.reservedMemberId && lesson.reservedMemberId !== user?.memberId && (
                              <Box
                                component="span"
                                sx={{
                                  px: '6px',
                                  py: '1px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  borderRadius: '4px',
                                  color: '#5B6472',
                                  backgroundColor: '#E7EAEF',
                                }}>
                                공유
                              </Box>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                      {/* 예약한 지점 = 그 수업이 열리는 지점. 다지점 공유 회원권이면 지금 보는 지점과 다를 수 있어
                          지금 보는 지점일 때 색으로 구분한다. */}
                      <TableCell
                        align="center"
                        title={lesson.branchName || undefined}
                        sx={{
                          ...bodyCellSx,
                          ...(otherBranch ? { color: '#7B8494' } : { color: '#1F6FEB', fontWeight: 600 }),
                        }}>
                        {lesson.branchName || '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ ...bodyCellSx, fontWeight: 500 }}>
                        {formatDate(lesson.classDateTime, DATE_FORMAT)}
                      </TableCell>
                      <TableCell align="center" sx={bodyCellSx}>{lesson.representativeTrainer || '-'}</TableCell>
                      <TableCell align="center" sx={bodyCellSx}>
                        <Stack sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                          <Box
                            sx={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              flexShrink: 0,
                              backgroundColor: meta?.color ?? '#8B95A3',
                            }}
                          />
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: meta?.color ?? '#5C6470' }}>
                            {meta?.label ?? t(lesson.status)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      {/* 예약자 — 출석·취소된 뒤에도 누가 예약했는지(처리자는 현재 상태를 만든 사람이다) */}
                      <TableCell
                        align="center"
                        title={formatActor(lesson.registeredByName, lesson.registeredByType)}
                        sx={{ ...bodyCellSx, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {lesson.registeredByName ? (
                          <>
                            {lesson.registeredByName}
                            {lesson.registeredByType && (
                              <Box component="span" sx={{ ml: '3px', fontSize: '12px', color: '#8A93A0' }}>
                                ({ACTOR_TYPE_LABELS[lesson.registeredByType] ?? lesson.registeredByType})
                              </Box>
                            )}
                          </>
                        ) : (
                          <Box component="span" sx={{ color: '#8A93A0' }}>
                            {formatActor(null, lesson.registeredByType)}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ ...bodyCellSx, color: '#7B8494' }}>
                        {lesson.processedDateTime ? formatDate(lesson.processedDateTime, PROCESSED_FORMAT) : '-'}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={bodyCellSx}
                        title={
                          lesson.processedByName
                            ? `${lesson.processedByName} (${ACTOR_TYPE_LABELS[lesson.processedByType] ?? lesson.processedByType})`
                            : undefined
                        }>
                        {lesson.processedByName ? (
                          <>
                            {lesson.processedByName}
                            {lesson.processedByType && (
                              <Box component="span" sx={{ ml: '3px', fontSize: '12px', color: '#8A93A0' }}>
                                ({ACTOR_TYPE_LABELS[lesson.processedByType] ?? lesson.processedByType})
                              </Box>
                            )}
                          </>
                        ) : (
                          <Box component="span" sx={{ color: '#8A93A0' }}>
                            {lesson.processedByType === 'SYSTEM' ? t('시스템') : '-'}
                          </Box>
                        )}
                      </TableCell>
                      {/* 처리지점 — 어느 지점에서 출결·취소를 눌렀는지. 과거 데이터·회원 본인 처리는 값이 없다. */}
                      <TableCell
                        align="center"
                        title={lesson.processedBranchName || undefined}
                        sx={{ ...bodyCellSx, color: '#7B8494' }}>
                        {lesson.processedBranchName || '-'}
                      </TableCell>
                      {/* 차감은 출결 확정 시점에 일어난다. 예약 행은 확정 시 빠질 몫을 미리 알려준다. */}
                      <TableCell align="center" sx={{ ...bodyCellSx, fontWeight: lesson.deductionCount ? 600 : 400 }}>
                        {lesson.deductionCount ? (
                          `${lesson.deductionCount}회`
                        ) : lesson.scheduledDeductionCount ? (
                          <Box component="span" sx={{ color: '#8A93A0' }}>
                            {`${lesson.scheduledDeductionCount}회 예정`}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      {/* 이력 — 행 클릭(스케줄 이동)과 겹치지 않게 전파를 막는다 */}
                      <TableCell align="center" sx={{ ...bodyCellSx, px: 0 }}>
                        <ReservationHistoryToggle
                          expanded={expanded}
                          onToggle={event => {
                            event.stopPropagation();
                            setExpandedId(prev => (prev === lesson.reservationId ? null : lesson.reservationId));
                          }}
                        />
                      </TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow>
                        <TableCell colSpan={isSharedTicket ? 13 : 12} sx={{ backgroundColor: '#FAFBFD', borderBottom: '1px solid #F2F4F8', pl: '60px' }}>
                          <ReservationEventTimeline memberId={user.memberId} reservationId={lesson.reservationId} token={bigUser.token} />
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            {/* 빈 안내는 표 밖에 둔다 — 표 안 빈 행은 가로 스크롤까지 포함한 표 전체 폭의 가운데라 오른쪽으로 밀려 보인다.
                표 밖 블록은 스크롤 영역의 보이는 폭을 갖고, sticky 로 스크롤해도 제자리에 남는다. */}
            {rows.length === 0 && (
              <Stack sx={{ position: 'sticky', left: 0, alignItems: 'center', gap: '8px', py: '72px' }}>
                <EventBusyIcon sx={{ fontSize: '32px', color: '#D4DAE1' }} />
                <Typography sx={{ fontSize: '13px', color: '#9AA1AC' }}>
                  {isAllMode
                    ? t('해당 이용권으로 예약된 수업이 없습니다.')
                    : t('선택한 상태의 내역이 없습니다.')}
                </Typography>
              </Stack>
            )}
          </TableContainer>
        )}
      </Box>

      {/* 페이지네이션이 바닥 액션 바에 붙지 않게 아래 여백을 준다 */}
      <Box sx={{ flexShrink: 0, pb: '14px' }}>
        <CommonPagination
          totalElements={lessons?.totalElements}
          limit={lessons?.size}
          page={page}
          onChange={(event, value) => setPage(value)}
        />
      </Box>

      {/* ── 바닥에 고정된 액션 바 ─────────────────────────────── */}
      <Stack
        sx={{
          flexShrink: 0,
          flexDirection: 'row',
          alignItems: 'center',
          gap: '8px',
          px: BAND_PX,
          py: '13px',
          borderTop: '1px solid #E4E8EE',
          backgroundColor: '#F7F9FC',
        }}>
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: hasSelection ? '#1F3F6B' : '#9AA1AC',
            whiteSpace: 'nowrap',
            mr: '4px',
          }}>
          {t('선택')} {selectedIds.length}{t('건')}
        </Typography>
        <Button
          variant="contained"
          disableElevation
          disabled={!hasSelection}
          onClick={() => openActionConfirm('attend')}
          sx={{ ...actionButtonSx, backgroundColor: '#2E7D32', '&:hover': { backgroundColor: '#276E2B' } }}>
          {t('출석 처리')}
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!hasSelection}
          onClick={() => openActionConfirm('miss')}
          sx={{ ...actionButtonSx, backgroundColor: '#C62828', '&:hover': { backgroundColor: '#AF2323' } }}>
          {t('결석 처리')}
        </Button>
        <Button
          variant="outlined"
          disabled={!hasSelection}
          onClick={() => openActionConfirm('cancel')}
          sx={{ ...actionButtonSx, color: '#5C6470', borderColor: '#C7CDD6', '&:hover': { borderColor: '#9AA1AC' } }}>
          {t('예약 취소')}
        </Button>
        {hasSelection && (
          <Button onClick={() => setSelectedIds([])} sx={{ ...actionButtonSx, color: '#7B8494', fontWeight: 500 }}>
            {t('선택 해제')}
          </Button>
        )}
        <Button
          variant="contained"
          disableElevation
          onClick={onClose}
          sx={{
            ...actionButtonSx,
            ml: 'auto',
            minWidth: '84px',
            color: '#2B3038',
            backgroundColor: '#E4E8EE',
            '&:hover': { backgroundColor: '#D6DCE5' },
          }}>
          {t('닫기')}
        </Button>
      </Stack>

      {/* 출결·취소 확인 — 선택 행 중 처리 대상만 집계해 보여준다 */}
      <CommonDeleteModal
        open={Boolean(pendingAction)}
        onClose={() => setPendingActionKey(null)}
        onConfirm={runPendingAction}
        title={pendingAction ? pendingAction.title(pendingTargets.length) : ''}
        description={pendingAction?.description}
        confirmText={pendingAction?.label}
        confirmVariant={pendingAction?.confirmVariant}
        isLoading={isSubmitting}
      />
    </Dialog>
  );
}
